'use client';

import { Box, BoxProps, Flex, freddyTheme, Typography } from '@coolblue-development/freddy';
import addBundleToCartTrigger from '@coolblue-development/ga4-events/triggers/Configurator/addBundleToCartTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { AddToCartButton } from '@coolblue-development/next-shopping-cart/client';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Product } from '@features/product/typings';
import type { MainProduct, SecondChanceProduct } from '@features/second-chance/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import { addConfiguratorProductsToShoppingCart } from '@utils/nextShoppingCartActions/addConfiguratorProductsToShoppingCart';
import { useRouter } from 'next/navigation';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { useConfigurator } from './ConfiguratorContext';
import ConfiguratorProductSlotQuantitySelector from './ConfiguratorProductSlotQuantitySelector';

type Props = {
  availabilityState?: ReactNode;
  product: Product | MainProduct;
  type?: 'overlay';
  quantitySelectorIndex?: number;
  secondChanceProduct?: SecondChanceProduct;
  isInCart?: boolean;
};

export default function ConfiguratorSummary({
  availabilityState,
  product,
  type,
  quantitySelectorIndex,
  secondChanceProduct,
  isInCart
}: Props) {
  const [averageDiscount, setAverageDiscount] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { trackEvent } = useAnalytics();
  const testId = useTestId('configurator-summary');
  const { t } = useTranslation('webshop.productPage.configurator');
  const atcButtonLabel = t('addToCart');
  const logger = useClientLogger();
  const { isMobile } = useBreakpoints();
  const router = useRouter();
  const {
    mainProduct,
    configurator,
    actions: { getProductByUseCaseAndSlot },
    states: { selectedUseCaseIndex, configuratorValues, checkboxValues }
  } = useConfigurator();

  const calculatePrice = useCallback(() => {
    const selectedConfigurator = configuratorValues.get(selectedUseCaseIndex) || [];
    const useCase = configurator.useCases[selectedUseCaseIndex];

    const {
      totalAccessoriesPrice,
      totalDiscountedAccessoriesPrice,
      totalDiscountedAccessoriesPriceWithPersonalDiscount
    } = useCase.slots.reduce(
      (totals, slot, index) => {
        const product = getProductByUseCaseAndSlot({
          useCaseIndex: selectedUseCaseIndex,
          slotIndex: index
        });

        const slotPersonalPrice = product?.personalPrice?.price.includingVat;
        const slotSalesPrice = product?.salesPrice?.includingVat || 0;
        const slotPrice = slotPersonalPrice || slotSalesPrice || 0;
        const configuratorItem = selectedConfigurator[index];

        if (configuratorItem?.selected) {
          const quantity = configuratorItem.quantity;
          const discountFactor = 1 - slot.discountPercentage / 100;

          totals.totalAccessoriesPrice += slotSalesPrice * quantity;
          totals.totalDiscountedAccessoriesPrice += slotSalesPrice * discountFactor * quantity;
          totals.totalDiscountedAccessoriesPriceWithPersonalDiscount +=
            slotPrice * discountFactor * quantity;
        }

        return totals;
      },
      {
        totalAccessoriesPrice: 0,
        totalDiscountedAccessoriesPrice: 0,
        totalDiscountedAccessoriesPriceWithPersonalDiscount: 0
      }
    );

    const basePersonalPrice = product?.personalPrice?.price.includingVat;
    const baseSalesPrice = product?.salesPrice?.includingVat || 0;
    const secondChanceBasePrice = secondChanceProduct?.prices?.salesPrice?.includingVat;
    const basePrice = secondChanceBasePrice || basePersonalPrice || baseSalesPrice || 0;

    setAverageDiscount(
      Math.round(
        ((totalAccessoriesPrice - totalDiscountedAccessoriesPrice) / totalAccessoriesPrice) * 100
      )
    );
    setTotalPrice(basePrice + totalAccessoriesPrice);
    setDiscountedPrice(
      totalDiscountedAccessoriesPrice === totalDiscountedAccessoriesPriceWithPersonalDiscount
        ? basePrice + totalDiscountedAccessoriesPrice
        : basePrice + totalDiscountedAccessoriesPriceWithPersonalDiscount
    );
  }, [
    configuratorValues,
    selectedUseCaseIndex,
    configurator.useCases,
    product?.personalPrice?.price.includingVat,
    product?.salesPrice?.includingVat,
    secondChanceProduct,
    getProductByUseCaseAndSlot
  ]);

  const hasDiscount = totalPrice > discountedPrice;

  useEffect(() => {
    calculatePrice();
  }, [selectedUseCaseIndex, configuratorValues, calculatePrice]);

  const trackingEvent = () => {
    const configuratorItemIds = configurator.useCases[selectedUseCaseIndex].slots
      .map((_, index) => {
        const product = getProductByUseCaseAndSlot({
          useCaseIndex: selectedUseCaseIndex,
          slotIndex: index
        });

        if (!checkboxValues.includes(String(index)) || !product?.id) {
          return;
        }

        return product.id;
      })
      .filter(Boolean);

    const params = {
      sub_feature:
        type === 'overlay' ? ('configurator_overlay' as const) : ('configurator' as const),
      primary_item_id: mainProduct.id,
      attached_item_ids: configuratorItemIds.join(',')
    };

    trackEvent(addBundleToCartTrigger(params));
  };

  const handleAddToCart = async () => {
    const selectedConfigurator = configuratorValues.get(selectedUseCaseIndex) || [];
    const configuratorProducts = configurator.useCases[selectedUseCaseIndex].slots.flatMap(
      (_, index) => {
        const product = getProductByUseCaseAndSlot({
          useCaseIndex: selectedUseCaseIndex,
          slotIndex: index
        });

        if (!checkboxValues.includes(String(index)) || !product?.id) {
          return [];
        }

        return {
          id: product.id,
          quantity: selectedConfigurator[index].quantity
        };
      }
    );

    setIsLoading(true);

    await addConfiguratorProductsToShoppingCart({
      useCaseId: configurator.useCases[selectedUseCaseIndex].id,
      mainProductId: mainProduct.id,
      complementaryProducts: configuratorProducts,
      secondChanceProductId: secondChanceProduct?.id
    })
      .then(({ redirectUrl }) => {
        trackingEvent();
        if (redirectUrl) {
          router.push(redirectUrl);
        }
      })
      .catch(error => {
        logger.error(
          `Failed to add configurator products to cart: product.id: ${mainProduct.id}, configurator useCaseId: ${configurator.useCases[selectedUseCaseIndex].id}`,
          error
        );
        setIsLoading(false);
      });
  };

  if (type === 'overlay') {
    if (isMobile) {
      return (
        <>
          <Box>
            {quantitySelectorIndex !== undefined && (
              <Flex mb={200} alignItems='center' gap={200}>
                <Typography variant='h5'>{t('quantity')}</Typography>
                <ConfiguratorProductSlotQuantitySelector index={quantitySelectorIndex} />
              </Flex>
            )}
            <Flex gap={100}>
              <Typography mb={0}>{t('totalPrice')}</Typography>
              {!!totalPrice && (
                <FadeInBox key={totalPrice}>
                  <SalesPrice
                    alignment='right'
                    currentPrice={<SalesPriceCurrent price={discountedPrice} />}
                    {...(hasDiscount && {
                      formerPrice: <SalesPriceFormer price={totalPrice} />
                    })}
                  />
                </FadeInBox>
              )}
            </Flex>
            <Flex gap={100} alignItems='center'>
              {!!averageDiscount && (
                <>
                  <Typography
                    component='div'
                    variant='h5'
                    color={freddyTheme.color.font.sentimentPositiveContrast}
                    mb={0}
                  >
                    {t('discount', { percentage: averageDiscount })}
                  </Typography>
                  <Typography>{t('onAccessories')}</Typography>
                </>
              )}
            </Flex>
          </Box>
          <AddToCartButton
            label={atcButtonLabel}
            loading={isLoading}
            onClick={handleAddToCart}
            isFullWidth
            disabled={isInCart}
          />
        </>
      );
    }

    return (
      <>
        <Flex direction='column' pr={600} flex={2}>
          <Flex gap={100}>
            <Typography mb={0}>{t('totalPrice')}</Typography>
            {!!totalPrice && (
              <FadeInBox key={totalPrice}>
                <SalesPrice
                  alignment='right'
                  currentPrice={<SalesPriceCurrent price={discountedPrice} />}
                  {...(hasDiscount && {
                    formerPrice: <SalesPriceFormer price={totalPrice} />
                  })}
                />
              </FadeInBox>
            )}
          </Flex>
          <Flex gap={100} alignItems='center'>
            {!!averageDiscount && (
              <>
                <Typography
                  variant='h5'
                  component='div'
                  color={freddyTheme.color.font.sentimentPositiveContrast}
                  mb={0}
                >
                  {t('discount', { percentage: averageDiscount })}
                </Typography>
                <Typography>{t('onAccessories')}</Typography>
              </>
            )}
          </Flex>
        </Flex>
        {quantitySelectorIndex !== undefined && (
          <Flex mr={400} alignItems='center' gap={200}>
            <Typography variant='h5'>{t('quantity')}</Typography>
            <ConfiguratorProductSlotQuantitySelector index={quantitySelectorIndex} />
          </Flex>
        )}
        <Flex flex={2}>
          <AddToCartButton
            label={atcButtonLabel}
            loading={isLoading}
            onClick={handleAddToCart}
            isFullWidth
            disabled={isInCart}
          />
        </Flex>
      </>
    );
  }

  return (
    <Flex
      {...testId}
      direction={{
        xs: 'column',
        md: 'row',
        lg: 'row',
        xl: 'column'
      }}
      gap={200}
    >
      <Flex flex={1} justifyContent='space-between' alignItems='center'>
        <Box>
          {!!averageDiscount && (
            <>
              <Typography
                variant='h5'
                color={freddyTheme.color.font.sentimentPositiveContrast}
                mb={0}
                component='div'
              >
                {t('discount', { percentage: averageDiscount })}
              </Typography>
              <Typography>{t('onAccessories')}</Typography>
            </>
          )}
        </Box>

        {!!totalPrice && (
          <Box>
            <FadeInBox key={totalPrice}>
              <SalesPrice
                alignment='right'
                currentPrice={<SalesPriceCurrent price={discountedPrice} />}
                {...(hasDiscount && {
                  formerPrice: <SalesPriceFormer price={totalPrice} />
                })}
              />
            </FadeInBox>
            {availabilityState && availabilityState}
          </Box>
        )}
      </Flex>

      <Flex flex={1}>
        <AddToCartButton
          label={atcButtonLabel}
          loading={isLoading}
          onClick={handleAddToCart}
          isFullWidth
          disabled={isInCart}
        />
      </Flex>
    </Flex>
  );
}

const fadeIn = keyframes`
  0% { opacity: 0 }
  50% { opacity: 1 }
  100% { opacity: 1 }
`;

export const FadeInBox = styled(Box)<BoxProps>`
  animation: ${fadeIn} 0.5s ease-in-out forwards;
`;
